import { useEffect, useState } from 'react';
import {
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { Button } from 'react-bootstrap';
import Flex from '@/components/common/Flex';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import {
  useGetInvoiceQuery,
  useShowFactorQuery
} from '@/app/Purchase/api/ordersApi';
import numberHumanizer from '@/helpers/numberHumanizer';
import bgFactor from '../assets/mizfa-tools-v1-low.svg';
import type { OrdersApiResponse } from '../types/ordersApiTypes';
import InvoicePreload from '../components/InvoicePreload';

function Invoice() {
  const { invoice, order_id } = useParams<{ invoice?: string; order_id?: string }>();
  const [factorInfo, setFactorInfo] = useState<OrdersApiResponse<Record<string, unknown>> | null>(
    null,
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Validate weekly_updates parameter - redirect to 404 if missing or invalid
  useEffect(() => {
    const weeklyUpdates = searchParams.get('wu');
    if (weeklyUpdates === null || weeklyUpdates === '') {
      navigate('/errors/404', { replace: true });
      return;
    }
    const weeklyUpdatesNum = Number(weeklyUpdates);
    if (weeklyUpdatesNum !== 3 && weeklyUpdatesNum !== 6) {
      navigate('/errors/404', { replace: true });
    }
  }, [searchParams, navigate]);

  const { data: userInfo, isLoading } = useGetUserInfoQuery();
  const { data: invoiceRes, isFetching } = useGetInvoiceQuery(
    {
      tariff_slug: invoice,
      introduced_code: searchParams.get('inc'),
      weekly_updates: Number(searchParams.get('wu')) || null,
    },
    { skip: !userInfo || Boolean(order_id) },
  );
  const { data: factorRes, isFetching: isFetchingFactor } = useShowFactorQuery(
    {
      order_id: order_id ?? '',
    },
    { skip: !userInfo || Boolean(invoice) },
  );
  
  useEffect(() => {
    if (!isFetchingFactor && !isFetching) {
      invoiceRes ? setFactorInfo(invoiceRes) : setFactorInfo(factorRes);
    }
  }, [invoiceRes, factorRes, isFetchingFactor, isFetching]);
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const wrapper = document.getElementById('print-page');
      if (wrapper && e.target instanceof Node && wrapper.contains(e.target)) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const printFactor = () => {
    const printContents = document.getElementById('print-wrapper')?.innerHTML;
    if (!printContents) return;
    document.body.innerHTML = printContents;

    window.print();
    location.reload();
  };
  if (isLoading || isFetching || isFetchingFactor || !factorInfo) {
    return <InvoicePreload />;
  }

  return (
      <div
        style={{
          height: '100vh'
        }}
        id="print-page"
      >
        <style>
          {`
  #print-page {
  -webkit-user-select: none;
  user-select: none;
  }
  @media print {
    @page {
    size: landscape;
    }
  }
        `}
        </style>
        <Flex className="gap-3 my-3 justify-content-center">
          <Button onClick={() => printFactor()} variant="falcon-primary">
            پرینت فاکتور
          </Button>
          <Button onClick={() => window.close()} variant="secondary">
            بستن
          </Button>
        </Flex>
        <div id="print-wrapper">
          <div id="factor-styles">
            <div
              id="factor_container"
              className="y-box y-box-lg y-box-secondary"
            >
              <div className="y-box-body" style={{ overflow: 'hidden' }}>
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  <div style={{ overflow: 'hidden', position: 'relative' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <div style={{ minWidth: '900px' }}>
                        <div>
                          <div className="align-items-center justify-content-center mx-0 mb-1">
                            <div className="row g-0">
                              <div className="col col-3">
                                <img
                                  src={bgFactor}
                                  style={{
                                    height: '45px',
                                    marginTop: '2px'
                                  }}
                                />
                              </div>
                              <div
                                className="col col-6"
                                style={{ textAlign: 'center' }}
                              >
                                <h1
                                  style={{
                                    fontSize: '19px',
                                    margin: '15px 0px',
                                    color: 'inherit'
                                  }}
                                >
                                  پیش فاکتور
                                </h1>
                              </div>
                              <div
                                className="col col-3"
                                style={{ textAlign: 'left' }}
                              >
                                <span>
                                  <b>شماره سریال:</b>
                                </span>{' '}
                                <span
                                  className="invoice-serialNumber"
                                  style={{ color: '#141414' }}
                                ></span>{' '}
                                <div className="mt-1">
                                  <span className="ml-1">
                                    <b>تاریخ:</b>
                                  </span>{' '}
                                  <span className="invoice-serialNumber">
                                    {factorInfo?.data?.created_at}
                                  </span>
                                </div>{' '}
                              </div>
                            </div>
                          </div>
                          <h2 className="factor_section_header">
                            مشخصات فروشنده
                          </h2>
                          <div className="y-invoice-sectionContentContainer">
                            <table className="y-invoice-sectionContent">
                              <tbody>
                                <tr>
                                  <th
                                    colspan="2"
                                    style={{ textAlign: 'right' }}
                                  >
                                    نام شخص حقیقی/حقوقی:{' '}
                                    {factorInfo?.data?.seller?.name}
                                  </th>
                                  <th className="text-left">شماره اقتصادی: </th>
                                  <td>
                                    {factorInfo?.data?.seller?.economical_number
                                      ? factorInfo?.data?.seller
                                          ?.economical_number
                                      : '-'}
                                  </td>
                                  <th className="text-left">کد ثبت:</th>
                                  <td>
                                    {factorInfo?.data?.seller?.registration_code
                                      ? factorInfo?.data?.seller
                                          ?.registration_code
                                      : '-'}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <b> استان:</b>
                                    <span>
                                      {' '}
                                      {factorInfo?.data?.seller?.province}{' '}
                                    </span>
                                  </td>
                                  <td>
                                    <b>شهر:</b>
                                    <span>
                                      {' '}
                                      {factorInfo?.data?.seller?.city}{' '}
                                    </span>
                                  </td>
                                  <th>کد پستی:</th>
                                  <td>
                                    {' '}
                                    {factorInfo?.data?.seller?.postal_code}{' '}
                                  </td>
                                  <th
                                    className="text-left"
                                    style={{
                                      marginRight: '-17px',
                                      display: 'grid'
                                    }}
                                  >
                                    شناسه ملی:
                                  </th>
                                  <td>
                                    {factorInfo?.data?.seller?.national_id
                                      ? factorInfo?.data?.seller?.national_id
                                      : '-'}
                                  </td>
                                </tr>
                                <tr>
                                  <td colspan="5">
                                    <b>نشانی: </b>
                                    <span style={{ fontSize: '15px' }}>
                                      {factorInfo?.data?.seller?.address}
                                    </span>
                                  </td>
                                  <th className="text-left">تلفن:</th>
                                  <td>
                                    {' '}
                                    {factorInfo?.data?.seller?.phone_number}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <h2 className="factor_section_header">
                            مشخصات خریدار
                          </h2>
                          <div className="y-invoice-sectionContentContainer">
                            <table className="y-invoice-sectionContent">
                              <tbody>
                                <tr>
                                  <th
                                    colspan="2"
                                    style={{ textAlign: 'right' }}
                                  >
                                    نام شخص حقیقی/حقوقی:{' '}
                                    {factorInfo?.data?.name}
                                  </th>
                                  <th className="text-left">شماره اقتصادی: </th>
                                  <td>
                                    {factorInfo?.data?.economical_number
                                      ? factorInfo?.data?.economical_number
                                      : '-'}
                                  </td>
                                  <th className="text-left">کد ثبت:</th>
                                  <td>
                                    {factorInfo?.data?.registration_code
                                      ? factorInfo?.data?.registration_code
                                      : '-'}
                                  </td>
                                </tr>
                                <tr>
                                  <td>
                                    <b> استان:</b>
                                    <span> {factorInfo?.data?.province} </span>
                                  </td>
                                  <td>
                                    <b>شهر:</b>
                                    <span> {factorInfo?.data?.city} </span>
                                  </td>
                                  <th>کد پستی:</th>
                                  <td> {factorInfo?.data?.postal_code} </td>
                                  <th
                                    className="text-left"
                                    style={{
                                      marginRight: '-17px',
                                      display: 'grid'
                                    }}
                                  >
                                    شناسه ملی:
                                  </th>
                                  <td>
                                    {factorInfo?.data?.national_id
                                      ? factorInfo?.data?.national_id
                                      : '-'}
                                  </td>
                                </tr>
                                <tr>
                                  <td colspan="5">
                                    <b>نشانی: </b>
                                    <span style={{ fontSize: '15px' }}>
                                      {factorInfo?.data?.address}
                                    </span>
                                  </td>
                                  <th className="text-left">تلفن:</th>
                                  <td> {factorInfo?.data?.phone_number}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <h1 className="factor_section_header">
                          مشخصات کالا یا خدمات مورد معامله
                        </h1>
                        <div
                          style={{
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          <table className="factor_payment_table">
                            <thead>
                              <tr style={{ fontSize: '13px' }}>
                                <th>
                                  <div
                                    style={{
                                      transform:
                                        'rotate(-90deg) translate(6px, -8px)',
                                      width: '15px'
                                    }}
                                  >
                                    ردیف
                                  </div>
                                </th>
                                <th>شـرح کـالا یا خدمـات</th>
                                <th>
                                  تعداد /<br />
                                  مقدار
                                </th>
                                <th>
                                  مبلغ کل
                                  <br />
                                  (ریال)
                                </th>
                                <th>
                                  مبلغ
                                  <br />
                                  تخفیف
                                  <br />
                                  (ریال)
                                </th>
                                <th>
                                  مبلغ کد معرف و
                                  <br />
                                  باقی مانده از طرح
                                  <br />
                                  (ریال)
                                </th>

                                <th>
                                  جمع مالیات و<br />
                                  عوارض
                                  <br />
                                  (ریال)
                                </th>
                                <th>
                                  جمع مبلغ کل بعلاوه
                                  <br />
                                  جمع مالیات و عوارض
                                  <br />
                                  (ریال)
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>1</td>
                                <td className="text-nowrap">
                                  خرید {factorInfo?.data?.order}
                                </td>
                                <td>{factorInfo?.data?.num_of_order}</td>
                                <td>
                                  {numberHumanizer(
                                    factorInfo?.data?.price_in_number
                                  )}
                                </td>
                                <td>
                                  {numberHumanizer(factorInfo?.data?.discount)}
                                </td>
                                <td>
                                  {numberHumanizer(
                                    factorInfo?.data?.intro_code_discount
                                  )}
                                </td>
                                <td>
                                  {numberHumanizer(
                                    factorInfo?.data?.tax_amount
                                  )}
                                </td>
                                <td>
                                  {numberHumanizer(
                                    factorInfo?.data?.payment_in_number
                                  )}
                                </td>
                              </tr>
                              <tr className="position-relative">
                                <td>
                                  <div
                                    style={{
                                      border: '2px solid black',
                                      position: 'absolute',
                                      right: '0px',
                                      left: '125px',
                                      top: '50%'
                                    }}
                                  ></div>
                                  <div
                                    style={{
                                      border: '2px solid black',
                                      position: 'absolute',
                                      width: '105px',
                                      left: '125px',
                                      top: '50%',
                                      transformOrigin: 'left center',
                                      transform: 'rotate(141deg)'
                                    }}
                                  ></div>
                                  2
                                </td>
                                <td className="text-nowrap"></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>3</td>
                                <td className="text-nowrap"></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>4</td>
                                <td className="text-nowrap"></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td colspan="7" className="w-50">
                                  <div style={{ textAlign: 'right' }}>
                                    <b>جمع کل:</b>{' '}
                                    <span className="float-left">
                                      {' '}
                                      <i style={{ fontSize: '12px' }}>*****</i>
                                      <span style={{ fontSize: '14px' }}>
                                        {factorInfo?.data?.payment_in_letter}{' '}
                                        ریال
                                      </span>
                                      <i
                                        className=""
                                        style={{ fontSize: '12px' }}
                                      >
                                        *****
                                      </i>
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  {numberHumanizer(
                                    factorInfo?.data?.payment_in_number
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td colspan="5">
                                  <span className="d-flex align-items-center justify-content-center">
                                    اعتبار این پیش‌فاکتور ۱ روز پس از تاریخ صدور
                                    می‌باشد.
                                  </span>{' '}
                                </td>
                                <td colspan="7" style={{ padding: '12px' }}>
                                  <div className="font-weight-bold">
                                    <span style={{ float: 'right' }}></span>
                                    <span
                                      style={{
                                        fontSize: '12px',
                                        float: 'left'
                                      }}
                                    >
                                      شماره شبا :{' '}
                                      {factorInfo?.data?.seller
                                        ?.sheba_number ? (
                                        factorInfo?.data?.seller?.sheba_number
                                      ) : (
                                        <span>IR490120020000008781295212</span>
                                      )}
                                      <br />
                                      {factorInfo?.data?.seller?.bank_name ? (
                                        factorInfo?.data?.seller?.bank_name
                                      ) : (
                                        <span>بانک ملت</span>
                                      )}
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="w-100 d-flex justify-content-end"></div>
                  </div>
                </div>

                <link rel="manifest" href="file:///C:/manifest.json" />
              </div>
              <img
                src="https://static.mizfa.tools/img/price-table/pish-factor2.png"
                alt=""
                style={{
                  opacity: 0.09,
                  position: 'absolute',
                  top: 8,
                  right: '50%',
                  transform: 'translate(50%,0)',
                  height: '626px'
                }}
              />
            </div>
          </div>
        </div>
      </div>
  );
};

export default Invoice;
